import { formatThaiDatetime } from "@aera/data-table";

export type GoogleIntegrationStatus = {
  connected: boolean | null;
  sheetsByWorkspace: Record<string, string>;
  driveFolderUrl?: string | null;
  email?: string;
  grantedAt?: string;
  /** Grant row exists in market-api (may still need reconnect). */
  hasGrant?: boolean;
  /** Grant permanently unusable — UI must force reconnect. */
  reconnectRequired?: boolean;
};

function unwrapTimestamp(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value !== "object") return value;
  if ("value" in value) {
    return unwrapTimestamp((value as { value: unknown }).value);
  }
  return value;
}

function normalizeTimestamp(value: unknown): string | undefined {
  const raw = unwrapTimestamp(value);
  if (raw == null) return undefined;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    const ms = raw < 1_000_000_000_000 ? raw * 1000 : raw;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? undefined : raw.toISOString();
  }

  if (typeof raw === "string") {
    const text = raw.trim();
    if (!text) return undefined;
    const asNumber = Number(text);
    if (Number.isFinite(asNumber)) {
      return normalizeTimestamp(asNumber);
    }
    const parsed = Date.parse(text);
    return Number.isFinite(parsed) ? new Date(parsed).toISOString() : text;
  }

  return undefined;
}

function pickTimestamp(
  res: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const normalized = normalizeTimestamp(res[key]);
    if (normalized) return normalized;
  }
  return undefined;
}

function normalizeSheetUrlValue(raw: unknown): string | null {
  if (raw == null) return null;

  if (typeof raw === "string") {
    const text = raw.trim();
    return text.length > 0 ? text : null;
  }

  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of [
      "url",
      "link",
      "href",
      "webViewLink",
      "spreadsheetUrl",
      "spreadsheet_url",
    ]) {
      const value = obj[key];
      if (typeof value === "string" && value.trim().length > 0) {
        return value.trim();
      }
    }
  }

  return null;
}

function isSpreadsheetUrl(url: string): boolean {
  return /docs\.google\.com\/spreadsheets\//i.test(url);
}

function extractSheetsByWorkspace(
  res: Record<string, unknown>,
): Record<string, string> {
  const sheetRaw =
    res.sheet ?? res.Sheet ?? res.spreadsheets ?? res.spreadsheetUrls;

  if (sheetRaw == null) {
    return {};
  }

  if (typeof sheetRaw === "string") {
    const url = normalizeSheetUrlValue(sheetRaw);
    return url ? { __legacy__: url } : {};
  }

  if (typeof sheetRaw !== "object" || Array.isArray(sheetRaw)) {
    return {};
  }

  const sheets: Record<string, string> = {};
  for (const [workspaceId, value] of Object.entries(
    sheetRaw as Record<string, unknown>,
  )) {
    const url = normalizeSheetUrlValue(value);
    if (url) {
      sheets[workspaceId.trim()] = url;
    }
  }

  return sheets;
}

function parseDriveFolderUrl(res: Record<string, unknown>): string | null {
  const connectedRaw = res.connected;
  if (typeof connectedRaw !== "string") return null;

  const url = connectedRaw.trim();
  if (!url) return null;

  if (/drive\.google\.com/i.test(url)) {
    return url;
  }

  return null;
}

function parseConnectedFlag(
  res: Record<string, unknown>,
  sheetsByWorkspace: Record<string, string>,
): boolean | null {
  const connectedRaw = res.connected as unknown;

  if (connectedRaw === true || connectedRaw === false || connectedRaw === null) {
    return connectedRaw;
  }

  if (typeof connectedRaw === "string") {
    return connectedRaw.trim().length > 0 ? true : false;
  }

  if (res.hasGrant === true || res.hasGrant === false) {
    return Boolean(res.hasGrant);
  }

  if (Object.keys(sheetsByWorkspace).length > 0) {
    return true;
  }

  return null;
}

export function resolveGoogleConnectionTimestamp(
  status: GoogleIntegrationStatus | null | undefined,
): string | null {
  const direct = normalizeTimestamp(status?.grantedAt);
  if (direct) return direct;
  return null;
}

export function parseGoogleIntegrationStatus(
  res: Record<string, unknown>,
): GoogleIntegrationStatus {
  const sheetsByWorkspace = extractSheetsByWorkspace(res);
  const connected = parseConnectedFlag(res, sheetsByWorkspace);
  const driveFolderUrl = parseDriveFolderUrl(res);

  const grantedAt = pickTimestamp(res, [
    "grantedAt",
    "granted_at",
    "connectedAt",
    "connected_at",
    "linkedAt",
    "linked_at",
    "updatedAt",
    "updated_at",
    "createdAt",
    "created_at",
    "lastConnectedAt",
    "last_connected_at",
  ]);

  return {
    connected,
    sheetsByWorkspace,
    driveFolderUrl,
    email: res.email as string | undefined,
    grantedAt,
    hasGrant: Boolean(res.hasGrant ?? res.has_grant),
    reconnectRequired: Boolean(
      res.reconnectRequired ?? res.reconnect_required,
    ),
  };
}

export function getGoogleSheetUrl(
  status: GoogleIntegrationStatus | null | undefined,
  workspaceId?: string | null,
  options?: { strictWorkspace?: boolean },
): string | null {
  if (!status) return null;

  const id = workspaceId?.trim();
  if (id) {
    const workspaceSheet = status.sheetsByWorkspace[id]?.trim();
    if (workspaceSheet) return workspaceSheet;
    if (options?.strictWorkspace) return null;
  }

  const legacySheet = status.sheetsByWorkspace.__legacy__?.trim();
  return legacySheet || null;
}

/** Sheets reconnect: workspace must have its own sheet URL (no `__legacy__` fallback). */
export function isGoogleWorkspaceSheetsConnected(
  status: GoogleIntegrationStatus | null | undefined,
  workspaceId?: string | null,
): boolean {
  const id = workspaceId?.trim();
  const sheetUrl = id
    ? getGoogleSheetUrl(status, id, { strictWorkspace: true })
    : getGoogleSheetUrl(status, null);
  return Boolean(sheetUrl && isSpreadsheetUrl(sheetUrl));
}

export type GoogleConnectionTone =
  | "connected"
  | "pending-link"
  | "needs-reconnect"
  | "disconnected"
  | "sync-failed";

export type GoogleSheetSyncHealth =
  | "synced"
  | "failed"
  | "pending"
  | "needs_reconnect"
  | "needs_provision"
  | null;

export type GoogleConnectionPresentation = {
  label: string;
  isConnected: boolean;
  hasSheetLink: boolean;
  tone: GoogleConnectionTone;
};

export function getGoogleConnectionPresentationStyle(
  tone: GoogleConnectionTone,
): {
  badgeClassName: string;
  textClassName: string;
  iconClassName: string;
} {
  switch (tone) {
    case "connected":
      return {
        badgeClassName: "bg-aera-greenlight text-aera-green",
        textClassName: "text-aera-green",
        iconClassName: "text-aera-green",
      };
    case "pending-link":
      return {
        badgeClassName: "bg-aera-warmcream text-aera-amberdark",
        textClassName: "text-aera-amberdark",
        iconClassName: "text-aera-amberdark",
      };
    case "sync-failed":
      return {
        badgeClassName: "bg-aera-redlight text-aera-red",
        textClassName: "text-aera-red",
        iconClassName: "text-aera-red",
      };
    case "needs-reconnect":
      return {
        badgeClassName: "bg-aera-redlight text-aera-red",
        textClassName: "text-aera-red",
        iconClassName: "text-aera-red",
      };
    case "disconnected":
      return {
        badgeClassName: "bg-aera-redlight text-aera-red",
        textClassName: "text-aera-red",
        iconClassName: "text-aera-red",
      };
  }
}

export function getGoogleConnectionPresentation(
  status: GoogleIntegrationStatus | null | undefined,
  workspaceId?: string | null,
  options?: { sheetSyncHealth?: GoogleSheetSyncHealth },
): GoogleConnectionPresentation {
  const connected = status?.connected ?? null;
  const sheetUrl = getGoogleSheetUrl(status, workspaceId);
  const hasSheetLink = Boolean(sheetUrl && isSpreadsheetUrl(sheetUrl));
  const sheetSyncHealth = options?.sheetSyncHealth ?? null;

  if (status?.reconnectRequired) {
    return {
      label: "ต้องเชื่อมใหม่",
      isConnected: false,
      hasSheetLink: false,
      tone: "needs-reconnect",
    };
  }

  if (hasSheetLink) {
    // G2: OAuth + sheet link still ok, but Live Mirror rewrite failed.
    if (sheetSyncHealth === "failed") {
      return {
        label: "ซิงก์ไม่สำเร็จ",
        isConnected: true,
        hasSheetLink: true,
        tone: "sync-failed",
      };
    }
    return {
      label: "เชื่อมต่อแล้ว",
      isConnected: true,
      hasSheetLink: true,
      tone: "connected",
    };
  }

  if (connected === true) {
    return {
      label: "ยังไม่สร้างชีต",
      isConnected: true,
      hasSheetLink: false,
      tone: "pending-link",
    };
  }

  if (connected === false || connected === null) {
    // Grant row left behind after invalid_grant / unauthorized_client.
    if (status?.hasGrant || status?.email) {
      return {
        label: "ต้องเชื่อมใหม่",
        isConnected: false,
        hasSheetLink: false,
        tone: "needs-reconnect",
      };
    }
    return {
      label: "ยังไม่ได้เชื่อมต่อ",
      isConnected: false,
      hasSheetLink: false,
      tone: "disconnected",
    };
  }

  return {
    label: "ยังไม่ได้เชื่อมต่อ",
    isConnected: false,
    hasSheetLink: false,
    tone: "disconnected",
  };
}

/**
 * Sheets reconnect pass — `connected` (has sheet URL) and `pending-link`
 * (Google grant ok, sheet not linked yet) count as the same outcome.
 */
export function isGoogleWorkspaceSheetsReconnectPass(
  status: GoogleIntegrationStatus | null | undefined,
  workspaceId?: string | null,
): boolean {
  const { tone } = getGoogleConnectionPresentation(status, workspaceId);
  return tone === "connected" || tone === "pending-link";
}

function getHoursSince(iso: string): number | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return (Date.now() - date.getTime()) / (1000 * 60 * 60);
}

/** Within 24h: relative hours (ชม.) · 24h+: Thai date/time */
export function formatGoogleSheetsActivityTime(
  iso: string | null | undefined,
): string {
  if (!iso?.trim()) return "—";

  const hoursSince = getHoursSince(iso);
  if (hoursSince == null) return "—";
  if (hoursSince < 0) return "—";

  if (hoursSince < 1 / 60) return "เมื่อสักครู่";
  if (hoursSince < 1) {
    return `${Math.floor(hoursSince * 60)} นาทีที่แล้ว`;
  }
  if (hoursSince < 24) {
    return `${Math.floor(hoursSince)} ชม.`;
  }

  const formatted = formatThaiDatetime(iso);
  return formatted !== "—" ? `${formatted} น.` : "—";
}

export function formatGoogleSheetsLastActivity(
  status: GoogleIntegrationStatus | null | undefined,
  latestActivityAt?: string | null,
): string {
  const { isConnected } = getGoogleConnectionPresentation(status);
  if (!isConnected) return "ยังไม่มีกิจกรรม";

  const connectedAt = resolveGoogleConnectionTimestamp(status);
  if (connectedAt) {
    return formatGoogleSheetsActivityTime(connectedAt);
  }

  const recentAt = latestActivityAt?.trim();
  if (recentAt) {
    return formatGoogleSheetsActivityTime(recentAt);
  }

  return "เมื่อสักครู่";
}

export function maskEmailForDisplay(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return "";

  const atIndex = trimmed.indexOf("@");
  if (atIndex === -1) {
    return `${trimmed.slice(0, 4)}****`;
  }

  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex);
  return `${local.slice(0, 4)}****${domain}`;
}
